'use client';
import React from 'react';
import {
  TwitterShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  EmailIcon,
  LinkedinIcon,
  BlueskyShareButton,
  XIcon,
  BlueskyIcon,
} from 'react-share';
import { runningClientSide, formatTwitterText, appendQueryParams, formatBlueSkyText } from '@benzinga/utils';
import styled from '@benzinga/themetron';
import { Icon, Tooltip } from '@benzinga/core-ui';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';

import { useSocialShare } from '@benzinga/hooks';
import { TrackingManager } from '@benzinga/tracking-manager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedin,
  faSquareFacebook,
  faSquareWhatsapp,
  faSquareXTwitter,
  faSquareBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelopeSquare } from '@fortawesome/pro-solid-svg-icons';
import { SessionContext } from '@benzinga/session-context';

type ShareButtonType = 'whatsapp' | 'linkedin' | 'twitter' | 'facebook' | 'email' | 'copy' | 'bluesky';

export interface ShareButtonsProps {
  buttonLocation: string;
  className?: string;
  tickers?: string[];
  title?: string;
  url?: string;
  showFontAwesomeIcons?: boolean;
  /**
   * @deprecated Use showButtons instead
   */
  showWhatsAppIcon?: boolean;
  buttonStyle?: Omit<React.SVGProps<SVGSVGElement>, 'width' | 'height'> & {
    bgStyle?: React.CSSProperties | undefined;
    borderRadius?: number | undefined;
    iconFillColor?: string | undefined;
    round?: boolean | undefined;
    size?: string | number | undefined;
  };
  showButtons?: ShareButtonType[];
  utmSource?: string;
  emailButtonStyle?: ShareButtonsProps['buttonStyle'];
}

export const ShareButtons: React.FC<ShareButtonsProps> = React.memo(
  ({
    buttonLocation,
    buttonStyle = {},
    emailButtonStyle = {},
    showButtons = ['linkedin', 'twitter', 'facebook', 'bluesky', 'email', 'copy'],
    showFontAwesomeIcons,
    showWhatsAppIcon,
    tickers,
    title = '',
    url,
    utmSource,
  }) => {
    const session = React.useContext(SessionContext);
    const resolvedEmailButtonStyle = { ...buttonStyle, ...emailButtonStyle };
    const [shareUrl, setShareUrl] = React.useState(url ? appendQueryParams(url, { utm_source: utmSource }) : url);
    const [shareTitle, setShareTitle] = React.useState(title);
    const { handler } = useSocialShare({ url: shareUrl });
    const [tooltipVisible, setTooltipVisible] = React.useState(false);

    // TODO: Remove later - Handles legacy showWhatsAppIcon prop
    React.useEffect(() => {
      if (showWhatsAppIcon && !showButtons.includes('whatsapp')) {
        showButtons.push('whatsapp');
      }
    }, [showWhatsAppIcon, showButtons]);

    React.useEffect(() => {
      if (!runningClientSide()) return;
      if (!url) setShareUrl(appendQueryParams(window.location.href, { utm_source: utmSource }));
      if (!title) setShareTitle(window.document.title);
    }, [url, title, utmSource]);

    React.useEffect(() => {
      if (!tooltipVisible) return;
      const timer = window.setTimeout(() => setTooltipVisible(false), 2500);
      return () => window.clearTimeout(timer);
    }, [tooltipVisible]);

    const onShareClick = React.useCallback(
      (medium: string) => {
        session.getManager(TrackingManager).trackLinkEvent('click', {
          button_id: medium,
          button_label: medium,
          button_location: buttonLocation,
          button_type: 'navigation',
          link_type: 'social',
          link_url: shareUrl,
          outbound_ad_url: shareUrl,
        });
      },
      [buttonLocation, session, shareUrl],
    );

    const handleCopyLink = React.useCallback(() => {
      const success = handler('Copy');
      if (success) setTooltipVisible(true);
      onShareClick('Copy Link');
    }, [handler, onShareClick]);

    const shareText = `${shareTitle} ${shareUrl}`;
    const mailtoHref = React.useMemo(() => {
      const trimmedShareText = shareText.trim();
      const body = trimmedShareText || shareUrl;
      const query = ['subject', 'body']
        .map(key => {
          const value = key === 'subject' ? shareTitle : body;
          return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
        })
        .join('&');
      return `mailto:?${query}`;
    }, [shareTitle, shareText, shareUrl]);

    const via = 'Benzinga';

    if (!shareUrl) {
      return <div />;
    }

    return (
      <Container className="page-share-buttons">
        {showButtons.includes('whatsapp') && (
          <WhatsappShareButton aria-label="Share on WhatsApp" onClick={() => onShareClick('WhatsApp')} url={shareUrl}>
            <FontAwesomeIcon icon={faSquareWhatsapp} size="2xl" style={{ color: '#25D366' }} />
          </WhatsappShareButton>
        )}
        {showButtons.includes('linkedin') && (
          <LinkedinShareButton
            aria-label="Share on Linkedin"
            onClick={() => onShareClick('Linkedin')}
            source="Benzinga"
            title={shareTitle}
            url={shareUrl}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faLinkedin} size="2xl" style={{ color: '#0e76a8' }} />
            ) : (
              <LinkedinIcon size={30} {...(buttonStyle || {})} />
            )}
          </LinkedinShareButton>
        )}
        {showButtons.includes('twitter') && (
          <TwitterShareButton
            aria-label="Share on X"
            onClick={() => onShareClick('Twitter')}
            title={formatTwitterText({ text: shareTitle, tickers })}
            url={shareUrl}
            via={via}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareXTwitter} size="2xl" />
            ) : (
              <XIcon size={30} {...(buttonStyle || {})} />
            )}
          </TwitterShareButton>
        )}
        {showButtons.includes('facebook') && (
          <FacebookShareButton aria-label="Share on Facebook" onClick={() => onShareClick('Facebook')} url={shareUrl}>
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareFacebook} size="2xl" style={{ color: '#1877f2' }} />
            ) : (
              <FacebookIcon size={30} {...(buttonStyle || {})} />
            )}
          </FacebookShareButton>
        )}
        {showButtons.includes('bluesky') && (
          <BlueskyShareButton
            aria-label="Share on Bluesky"
            onClick={() => onShareClick('Bluesky')}
            title={formatBlueSkyText(shareTitle)}
            url={shareUrl}
          >
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faSquareBluesky} size="2xl" style={{ color: '#007bff' }} />
            ) : (
              <BlueskyIcon size={30} {...(buttonStyle || {})} />
            )}
          </BlueskyShareButton>
        )}
        {showButtons.includes('email') && (
          <MailtoShareLink aria-label="Share on Email" href={mailtoHref} onClick={() => onShareClick('Email')}>
            {showFontAwesomeIcons ? (
              <FontAwesomeIcon icon={faEnvelopeSquare} size="2xl" />
            ) : (
              <EmailIcon size={30} {...(resolvedEmailButtonStyle || {})} />
            )}
          </MailtoShareLink>
        )}
        {showButtons.includes('copy') && (
          <Tooltip content="Link copied!" size="sm" trigger="click" visible={tooltipVisible}>
            <CopyLinkButton
              $backgroundColor={resolvedEmailButtonStyle?.bgStyle?.fill}
              $borderRadius={resolvedEmailButtonStyle?.borderRadius}
              $color={resolvedEmailButtonStyle?.iconFillColor}
              $round={resolvedEmailButtonStyle?.round}
              $size={resolvedEmailButtonStyle?.size}
              aria-label={shareTitle ? `Copy link to ${shareTitle}` : 'Copy link'}
              className={`copy-link-button ${showFontAwesomeIcons ? 'bordered-square' : ''}`}
              onClick={handleCopyLink}
            >
              <Icon className="link-icon" icon={faLink} />
            </CopyLinkButton>
          </Tooltip>
        )}
      </Container>
    );
  },
);

const MailtoShareLink = styled.a`
  background-color: transparent;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
`;

const CopyLinkButton = styled.button<{
  $backgroundColor?: string;
  $color?: string;
  $borderRadius?: number;
  $round?: boolean;
  $size?: string | number;
}>`
  height: ${({ $size }) => (typeof $size === 'number' ? `${$size}px` : $size || '30px')};
  width: ${({ $size }) => (typeof $size === 'number' ? `${$size}px` : $size || '30px')};
  background-color: ${({ $backgroundColor }) => ($backgroundColor ? $backgroundColor : '#7f7f7f')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $borderRadius, $round, $size }) => {
    if ($round) return '50%';
    if ($borderRadius == null) return 'none';
    const renderedSize = typeof $size === 'number' ? $size : Number.parseFloat(String($size || '30')) || 30;
    return `${($borderRadius * renderedSize) / 64}px`;
  }};

  .link-icon {
    color: ${({ $color }) => ($color ? $color : '#ffffff')};
  }

  &.bordered-square {
    height: 29px;
    width: 28px;
    background-color: #000c;
    border-radius: 4px;
  }
`;

const Container = styled.div`
  &.page-share-buttons {
    display: flex;
    align-items: center;
    position: relative;
    height: 40px;
    gap: 0 5px;

    > button,
    > a,
    .copy-link-button {
      transition: transform 250ms;
      will-change: transform;

      &:hover {
        transform: translateY(-5px);
      }
    }
  }
`;
